import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import UserContext from '../../../components/User_Context.js';
import * as api from "../../../api/apiConnector.js";
import { Sections_Builder } from './sections_builder.js';
import { Simulation_Builder } from './compilerNew.js';
import UserGuideManager from './components/UserGuideManager.js';
import CollaborativeActorsManager from './components/CollaborativeActorsManager.js';
import { FaSave } from 'react-icons/fa';
import * as breadcrumb from "../../../components/ui/breadcrumb.js";
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import * as menu from "../../../assets/menus/menu.js";
import { useTranslation } from 'react-i18next';
import Choices from '../../../components/Choices.js';



// Define a consistent color palette for the entire form builder
const Colors = {
  // Primary brand
  primary: '#f05f23', // Orange
  primaryHover: '#d44d12',
  primaryLight: '#fef0ea',

  // Secondary neutral
  secondary: '#6c757d',
  secondaryLight: '#e9ecef',

  // Base colors
  dark: '#343a40',
  light: '#f8f9fa',
  white: '#ffffff',

  // Alert colors
  success: '#28a745',
  danger: '#dc3545',
  warning: '#ffc107',
  info: '#17a2b8',

  // Editing context colors - more transparent and softer
  editContextSection: {
    header: 'rgba(242, 120, 75, 0.7)',    // More transparent orange
    body: 'rgba(255, 236, 224, 0.3)',     // Very light orange, more transparent
    border: 'rgba(242, 120, 75, 0.4)',    // Semi-transparent orange border
    badge: 'rgba(242, 120, 75, 0.8)'      // Semi-transparent orange badge
  },

  editContextQuestion: {
    header: 'rgba(52, 152, 219, 0.7)',    // More transparent blue
    body: 'rgba(235, 245, 253, 0.3)',     // Very light blue, more transparent
    border: 'rgba(52, 152, 219, 0.4)',    // Semi-transparent blue border
    badge: 'rgba(52, 152, 219, 0.8)'      // Semi-transparent blue badge
  },

  editContextAnswer: {
    header: 'rgba(46, 204, 113, 0.7)',    // More transparent green
    body: 'rgba(232, 253, 241, 0.3)',     // Very light green, more transparent
    border: 'rgba(46, 204, 113, 0.4)',    // Semi-transparent green border
    badge: 'rgba(46, 204, 113, 0.8)'      // Semi-transparent green badge
  }
};

const Form_Builder = () => {
  const { id } = useParams();
  const { currentUser } = useContext(UserContext);
  const { t } = useTranslation();

  const [data, setData] = useState({ form: null });
  const [language, setLanguage] = useState('it');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const MySwal = withReactContent(Swal);
  const [showSimulation, setShowSimulation] = useState(false);
  const [groupsVoc, setGroupsVoc] = useState([]);
  const groupRef = useRef();
  const [preventQuit, setPreventQuit] = useState(false); // Track if there are unsaved changes



  useEffect(() => {
    (async () => {
      menu.write({ user: currentUser() });
    })();
  }, []);

  // Aggiungiamo il controllo per il beforeunload quando ci sono modifiche non salvate
  // ma non quando si è in simulazione
  useEffect(() => {
    if (preventQuit && !showSimulation) {
      const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = '';
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [preventQuit, showSimulation]);

  // Set breadcrumb for the page
  const setBreadCrumb = () => {
    const formName = data.form?.Name && data.form.Name.trim() !== ''
      ? data.form.Name
      : (data.form?.Id ? `#${data.form.Id}` : t('newForm', { ns: 'labels' }));

    const path = [
      { link: "/", name: t('home', { ns: 'labels' }) },
      { link: `/form_builder/${id || ''}`, name: `${t('form', { ns: 'labels' })}: ${formName}` }
    ];
    breadcrumb.set(path);
  };


  // Load form data and vocabularies on mount
  useEffect(() => {
    const loadForm = async () => {
      try {
        // Load form data
        const result = await api.sendMessage("darm/formBuilder/get", {
          user: currentUser(),
          body: { id: id || null }
        });

        if (result.success && result.form) {
          setData({ form: result.form });
        } else {
          setError("Unable to load the requested form.");
        }

        // Load group vocabulary
        const groupsResult = await api.sendMessage("admin/vocs/get", {
          user: currentUser(),
          body: { 
            voc_id: "form_groups" 
          }
        });


        if (groupsResult?.items) {
          setGroupsVoc(groupsResult.items);
        }
      } catch (err) {
        console.error("Error loading form:", err);
        setError("An error occurred while loading the form.");
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [id, currentUser]);

  // Update breadcrumb when form data changes
  useEffect(() => {
    if (data.form) {
      setBreadCrumb();
    }
  }, [data.form]);

  const clearEmptyCondition = (form) => {
    const cleanedForm = JSON.parse(JSON.stringify(form)); // Clone the obj
  
    cleanedForm.sections.forEach((section) => {
      section.questions.forEach((question) => {
        const condition = question.visibilityCondition;

        // If the visibility condition exists and its 'children' array is empty, clear the visibilityCondition by setting it to null
        if (condition && Array.isArray(condition.children) && condition.children.length === 0) {
           question.visibilityCondition = null;
        }
      });
    });
  
    return cleanedForm;
  };
  
  const updateField = (field, value) => {
    setData(prev => ({
      form: {
        ...prev.form,
        [field]: value
      }
    }));
    setPreventQuit(true);
  };

  const updateSectionTitle = (index, newTitleArray) => {
    const sections = [...(data.form.sections || [])];
    sections[index].name = newTitleArray; // ✅ ok

    setData(prev => ({
      form: {
        ...prev.form,
        sections
      }
    }));
    setPreventQuit(true);
  };

  const deleteSection = (index) => {
    const sections = [...(data.form.sections || [])];
    sections.splice(index, 1);
    setData(prev => ({
      form: {
        ...prev.form,
        sections
      }
    }));
    setPreventQuit(true);
  };

  const getGuid = async () => {
    try {
      const result = await api.sendMessage("helper/guidGenerator", {
        user: currentUser(),
        body: {}
      });

      if (result.success && result.guid) {
        return result.guid;
      }
      console.error("Error getting GUID:", result);
      return null;
    } catch (err) {
      console.error("Error calling GUID generator:", err);
      return null;
    }
  };

  // Nuovo metodo per ottenere entità vuote
  const getEmptyEntity = async (entityType) => {
    try {
      const result = await api.sendMessage("darm/form_builder/getEmptyEntity", {
        user: currentUser(),
        body: {
          type: entityType // 'section', 'question', 'answer'
        }
      });

      if (result.success && result.entity) {
        return result.entity;
      }
      console.error(`Error getting empty ${entityType}:`, result);
      
      // // Fallback in caso di errore nell'API
      // if (entityType === 'section') {
      //   return { name: [], description: [], questions: [], id: await getGuid(), invisible: false, sectionCode: "" };
      // } else if (entityType === 'question') {
      //   return { questionText: [], answers: [], id: await getGuid() };
      // } else if (entityType === 'answer') {
      //   return { answerText: [], id: await getGuid() };
      // }
      
      return null;
    } catch (err) {
      console.error(`Error calling empty ${entityType} generator:`, err);
      return null;
    }
  };

  const addSection = async () => {
    const emptySection = await getEmptyEntity('section');
    if (!emptySection) {
      console.error("Failed to get empty section");
      return;
    }
    
    // Generate a sectionCode based on the current number of sections
    const sectionIndex = data.form.sections ? data.form.sections.length : 0;
    emptySection.sectionCode = generateHierarchicalCodes.generateSectionCode(sectionIndex);
    
    const updatedSections = [...(data.form.sections || []), emptySection];
    
    setData(prev => ({
      form: {
        ...prev.form,
        sections: updatedSections
      }
    }));
    setPreventQuit(true);
  };

  /**
   * Validate that all section, question and answer codes are unique in the form
   * @param {object} form - The form object to validate
   * @returns {object} - {isValid: boolean, conflicts: array of string messages}
   */
  const validateUniqueCodes = (form) => {
    const conflicts = [];
    const allCodes = new Map();
    
    // Function to check and register a code
    const checkCode = (code, type, parentInfo) => {
      if (!code) return; // Skip empty codes
      
      if (allCodes.has(code)) {
        const existing = allCodes.get(code);
        conflicts.push(`${type} ${code} ${parentInfo} confligge con ${existing.type} ${existing.parentInfo}`);
      } else {
        allCodes.set(code, { type, parentInfo });
      }
    };
    
    // Check all section codes
    if (form.sections && Array.isArray(form.sections)) {
      form.sections.forEach((section, sIndex) => {
        checkCode(section.sectionCode, "Sezione", `(indice: ${sIndex + 1})`);
        
        // Check all question codes within this section
        if (section.questions && Array.isArray(section.questions)) {
          section.questions.forEach((question, qIndex) => {
            checkCode(
              question.questionCode, 
              "Domanda", 
              `(nella sezione ${section.sectionCode || sIndex + 1}, indice: ${qIndex + 1})`
            );
            
            // Check all answer codes within this question
            if (question.answers && Array.isArray(question.answers)) {
              question.answers.forEach((answer, aIndex) => {
                checkCode(
                  answer.answerCode, 
                  "Risposta", 
                  `(nella domanda ${question.questionCode || qIndex + 1}, indice: ${aIndex + 1})`
                );
              });
            }
          });
        }
      });
    }
    
    return {
      isValid: conflicts.length === 0,
      conflicts
    };
  };

  const saveForm = async () => {
    setSaving(true);
    setError(null);

    // Mostra immediatamente una modale di caricamento
    MySwal.fire({
      title: t('saving', { ns: 'labels' }) || 'Salvataggio in corso...',
      html: t('savingMessage', { ns: 'labels' }) || 'Attendere prego mentre il form viene salvato.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        MySwal.showLoading();
      },
      showConfirmButton: false
    });

    try {
      // Validate form name
      if (!data.form.Name || data.form.Name.trim() === '') {
        throw new Error(t('formNameRequired', { ns: 'labels' }));
      }
      
      // Validate form code
      if (!data.form.Code || data.form.Code.trim() === '') {
        throw new Error(t('formCodeRequired', { ns: 'labels' }));
      }
      
      // Check if a form with the same name or code already exists
      const existsCheckResult = await api.sendMessage("darm/formBuilder/checkExists", {
        user: currentUser(),
        body: {
          formName: data.form.Name,
          formCode: data.form.Code,
          id: data.form.Id || null // Exclude current form from check if editing
        }
      });
      
      if (existsCheckResult.success && existsCheckResult.exists) {
        // Create error message based on what exists
        let errorMessages = [];
        
        if (existsCheckResult.exists.nameExists) {
          errorMessages.push(t('formNameExists', { ns: 'labels' }) || 'Un form con questo nome esiste già.');
        }
        
        if (existsCheckResult.exists.codeExists) {
          errorMessages.push(t('formCodeExists', { ns: 'labels' }) || 'Un form con questo codice esiste già.');
        }
        
        if (errorMessages.length > 0) {
          // Chiudi la modale di caricamento e mostra l'errore
          MySwal.fire({
            title: t('error', { ns: 'labels' }),
            icon: "error",
            html: errorMessages.join('<br>'),
            showConfirmButton: true
          });
          
          setSaving(false);
          return; // Stop the save process
        }
      }
      
      // Validate unique codes
      const { isValid, conflicts } = validateUniqueCodes(data.form);
      if (!isValid) {
        // Create a formatted error message with all conflicts
        let errorMessage = t('duplicateCodesFound', { ns: 'labels' }) || 'Sono stati trovati codici duplicati:';
        errorMessage += '<ul>';
        conflicts.forEach(conflict => {
          errorMessage += `<li>${conflict}</li>`;
        });
        errorMessage += '</ul>';
        
        // Chiudi la modale di caricamento e mostra l'errore
        MySwal.fire({
          title: t('error', { ns: 'labels' }),
          icon: "error",
          html: errorMessage, // Use html instead of text to render the list
          showConfirmButton: true
        });
        
        setSaving(false);
        return; // Stop the save process
      }
      
      const cleanedData = clearEmptyCondition(data.form);
      
      // Format data according to the backend's expected structure
      const formDataToSave = {
        Id: data.form.Id,
        Name: data.form.Name || '',
        Description: data.form.Description || '',
        Group: data.form.Group || '',
        Code: data.form.Code || '',
        showFirstProgressBar: data.form.showFirstProgressBar ?? true,
        showSecondProgressBar: data.form.showSecondProgressBar ?? true,
        showUserGuide: data.form.showUserGuide ?? false,
        IsCollaborative: data.form.IsCollaborative ?? false,
        CollaborativeFormsSchema: data.form.CollaborativeFormsSchema || [],
        FormData: cleanedData
      };

      const result = await api.sendMessage("darm/formBuilder/save", {
        user: currentUser(),
        body: { form: formDataToSave }
      });

      if (result.success) {
        // Chiudi la modale di caricamento e mostra il messaggio di successo
        MySwal.fire({
          title: t('success', { ns: 'labels' }),
          icon: "success",
          text: result.create ? t('createSuccess', { ns: 'labels' }) : t('updateSuccess', { ns: 'labels' }),
          showConfirmButton: true,
          timer: 2000
        });
        
        // Reset preventQuit after successful save
        setPreventQuit(false);
      } else {
        throw new Error(result.error || t('saveError', { ns: 'labels' }));
      }
    } catch (err) {
      console.error("Save error:", err);

      // Improved error handling for non-JSON responses
      let errorMessage = t('saveError', { ns: 'labels' });
      if (err.message && err.message.includes("not valid JSON")) {
        errorMessage = t('invalidResponse', { ns: 'labels' });
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      // Chiudi la modale di caricamento e mostra l'errore
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        icon: "error",
        text: errorMessage,
        showConfirmButton: true
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteForm = () => {
    if (!data.form?.Id) {
      return;
    }

    MySwal.fire({
      title: t('attention', { ns: 'labels' }),
      text: t('confirmDelete', { ns: 'labels' }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f05f23',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('confirm', { ns: 'labels' }),
      cancelButtonText: t('cancel', { ns: 'labels' })
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.sendMessage("darm/formBuilder/delete", {
            user: currentUser(),
            body: {
              id: data.form.Id
            }
          });

          if (response.success) {
            MySwal.fire({
              title: t('success', { ns: 'labels' }),
              text: t('deleteSuccess', { ns: 'labels' }),
              icon: 'success',
              timer: 2000
            }).then(() => {
              // Reset preventQuit before redirecting
              setPreventQuit(false);
              // Redirect to the form finder page after successful deletion
              window.location.href = '/form_builder/finder';
            });
          } else {
            MySwal.fire({
              title: t('error', { ns: 'labels' }),
              text: response.message || t('deleteError', { ns: 'labels' }),
              icon: 'error'
            });
          }
        } catch (error) {
          MySwal.fire({
            title: t('error', { ns: 'labels' }),
            text: t('deleteError', { ns: 'labels' }),
            icon: 'error'
          });
        }
      }
    });
  };

  const reorderSections = (newSections) => {
    setData(prev => ({
      form: {
        ...prev.form,
        sections: newSections
      }
    }));
    setPreventQuit(true);
  };

  const updateSection = (index, updatedSection) => {
    const updatedSections = [...(data.form.sections || [])];

    updatedSections[index] = {
      ...updatedSections[index],
      ...updatedSection
    };

    setData(prev => ({
      form: {
        ...prev.form,
        sections: updatedSections
      }
    }));
    setPreventQuit(true);
  };

  const handleSimulation = () => {
    setShowSimulation(true);
  };

  const handleExportForm = () => {
    if (!data.form) {
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: t('noFormToExport', { ns: 'labels' }) || 'Nessun form da esportare',
        icon: 'error'
      });
      return;
    }

    // Check if there are unsaved changes
    if (preventQuit) {
      MySwal.fire({
        title: t('attention', { ns: 'labels' }),
        text: t('saveBeforeExport', { ns: 'labels' }) || 'Salva le modifiche prima di esportare il form',
        icon: 'warning',
        confirmButtonColor: '#f05f23',
        confirmButtonText: 'OK'
      });
      return;
    }

    try {
      // Clean the form data by removing empty conditions
      const cleanedFormData = clearEmptyCondition(data.form);
      
      // Create the export data structure
      const exportData = {
        exportDate: new Date().toISOString(),
        exportVersion: "1.0",
        formData: cleanedFormData
      };

      // Convert to JSON string with proper formatting
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create blob and download link
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      
      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const formName = data.form.Name || data.form.Code || data.form.Id || 'form';
      const filename = `${formName}_${timestamp}.json`;
      
      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Show success message
      MySwal.fire({
        title: t('success', { ns: 'labels' }),
        text: t('exportSuccess', { ns: 'labels' }) || 'Form esportato con successo',
        icon: 'success',
        timer: 2000
      });
      
    } catch (error) {
      console.error("Export error:", error);
      MySwal.fire({
        title: t('error', { ns: 'labels' }),
        text: error.message || t('exportError', { ns: 'labels' }) || 'Errore durante l\'esportazione del form',
        icon: 'error'
      });
    }
  };

  const handleImportForm = () => {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';
    fileInput.style.display = 'none';
    
    fileInput.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        // Show loading modal
        MySwal.fire({
          title: t('importing', { ns: 'labels' }) || 'Importazione in corso...',
          text: t('importingForm', { ns: 'labels' }) || 'Importazione del form in corso, attendere prego.',
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            MySwal.showLoading();
          },
          showConfirmButton: false
        });

        // Read file content
        const fileContent = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = (e) => reject(e);
          reader.readAsText(file);
        });

        // Parse JSON content
        let jsonData;
        try {
          jsonData = JSON.parse(fileContent);
        } catch (parseError) {
          throw new Error(t('invalidFileFormat', { ns: 'labels' }) || 'Formato file non valido. Il file deve essere un JSON valido.');
        }

        // Send the JSON data to the API
        const result = await api.sendMessage("darm/importForm", {
          user: currentUser(),
          body: { 
            form: jsonData,
            fileName: file.name 
          }
        });

        if (result.success && result.id) {
          MySwal.fire({
            title: t('success', { ns: 'labels' }),
            text: t('importSuccess', { ns: 'labels' }) || 'Form importato con successo',
            icon: 'success',
            timer: 2000
          }).then(() => {
            // Reset preventQuit before navigation
            setPreventQuit(false);
            // Redirect to the imported form using the ID returned by the backend
            window.location.href = `/form_builder/${result.id}`;
          });
        } else {
          throw new Error(result.error || t('importError', { ns: 'labels' }) || 'Errore durante l\'importazione');
        }
      } catch (error) {
        console.error("Import error:", error);
        MySwal.fire({
          title: t('error', { ns: 'labels' }),
          text: error.message || t('importError', { ns: 'labels' }) || 'Errore durante l\'importazione del form',
          icon: 'error'
        });
      } finally {
        // Clean up
        document.body.removeChild(fileInput);
      }
    };

    // Add to DOM and trigger click
    document.body.appendChild(fileInput);
    fileInput.click();
  };

  const handleSectionReorder = (reorderedSections) => {
    // Update all codes throughout the form to maintain hierarchy when sections are reordered
    const updatedSections = generateHierarchicalCodes.updateAllCodes(reorderedSections);
    setData(prev => ({
      form: {
        ...prev.form, 
        sections: updatedSections
      }
    }));
    setPreventQuit(true);
  };

  const cloneSection = async (index) => {
    try {
      // Get the section to clone
      const sectionToClone = { ...data.form.sections[index] };
      
      // Get a new empty section from backend to get fresh IDs
      const emptySection = await getEmptyEntity('section');
      if (!emptySection) {
        throw new Error("Failed to get empty section");
      }
      
      // Clone the section data while using new ID
      const clonedSection = {
        ...sectionToClone,
        id: emptySection.id,
        sectionCode: generateHierarchicalCodes.generateSectionCode(data.form.sections.length),
        questions: [] // We'll add cloned questions with new IDs
      };
      
      // Add "(cloned)" or "(clonata)" to section name based on language
      if (clonedSection.name && Array.isArray(clonedSection.name)) {
        clonedSection.name = clonedSection.name.map(nameItem => {
          const suffix = nameItem.language === 'it' ? ' (clonata)' : ' (cloned)';
          return {
            ...nameItem,
            text: nameItem.text + suffix
          };
        });
      }

      // Create mapping of original IDs to new IDs for conditions update
      const questionIdMap = new Map();
      const answerIdMap = new Map();
      
      // Clone each question in the section
      for (let i = 0; i < sectionToClone.questions.length; i++) {
        const questionToClone = sectionToClone.questions[i];
        
        // Get empty question to get new ID
        const emptyQuestion = await getEmptyEntity('question');
        if (!emptyQuestion) {
          continue; // Skip this question if we couldn't get a new ID
        }
        
        // Store mapping for condition updates
        questionIdMap.set(questionToClone.id, emptyQuestion.id);
        
        // Clone the question data while using new ID
        const clonedQuestion = {
          ...questionToClone,
          id: emptyQuestion.id,
          questionCode: generateHierarchicalCodes.generateQuestionCode(
            clonedSection.sectionCode, 
            clonedSection.questions.length
          ),
          answers: [] // We'll add cloned answers with new IDs
        };
        
        // Clone each answer in the question
        for (let j = 0; j < questionToClone.answers?.length || 0; j++) {
          const answerToClone = questionToClone.answers[j];
          
          // Get empty answer to get new ID
          const emptyAnswer = await getEmptyEntity('answer');
          if (!emptyAnswer) {
            continue; // Skip this answer if we couldn't get a new ID
          }
          
          // Store mapping for condition updates
          answerIdMap.set(answerToClone.id, emptyAnswer.id);
          
          // Clone the answer data while using new ID
          const clonedAnswer = {
            ...answerToClone,
            id: emptyAnswer.id,
            answerCode: generateHierarchicalCodes.generateAnswerCode(
              clonedQuestion.questionCode, 
              clonedQuestion.answers.length
            )
          };
          
          clonedQuestion.answers.push(clonedAnswer);
        }
        
        clonedSection.questions.push(clonedQuestion);
      }

      // Update conditions in all cloned questions to reference new IDs
      clonedSection.questions.forEach(question => {
        if (question.visibilityCondition) {
          question.visibilityCondition = updateConditionReferences(
            question.visibilityCondition, 
            questionIdMap, 
            answerIdMap
          );
        }
      });

      // Update section-level conditions if any
      if (clonedSection.visibilityCondition) {
        clonedSection.visibilityCondition = updateConditionReferences(
          clonedSection.visibilityCondition, 
          questionIdMap, 
          answerIdMap
        );
      }
      
      // Add the cloned section to the form
      const updatedSections = [...(data.form.sections || []), clonedSection];
      
      setData(prev => ({
        form: {
          ...prev.form,
          sections: updatedSections
        }
      }));
    } catch (err) {
      console.error("Error cloning section:", err);
    }
  };

  /**
   * Updates condition references to use new IDs from cloned questions/answers
   * @param {Object} condition - The condition object to update
   * @param {Map} questionIdMap - Map of original question IDs to new IDs
   * @param {Map} answerIdMap - Map of original answer IDs to new IDs
   * @returns {Object} Updated condition with new ID references
   */
  const updateConditionReferences = (condition, questionIdMap, answerIdMap) => {
    if (!condition) return condition;

    // Handle base condition - update questionId and answerId references
    if (condition.type === "CONDITION") {
      return {
        ...condition,
        questionId: questionIdMap.get(condition.questionId) || condition.questionId,
        answerId: answerIdMap.get(condition.answerId) || condition.answerId
      };
    }

    // Handle AND/OR conditions - recursively update children
    if (condition.type === "AND" || condition.type === "OR") {
      return {
        ...condition,
        children: condition.children.map(child => 
          updateConditionReferences(child, questionIdMap, answerIdMap)
        )
      };
    }

    return condition;
  };
  
  const cloneQuestion = async (sectionIndex, questionIndex) => {
    try {
      // Get the question to clone
      const questionToClone = { ...data.form.sections[sectionIndex].questions[questionIndex] };
      
      // Get a new empty question from backend to get fresh ID
      const emptyQuestion = await getEmptyEntity('question');
      if (!emptyQuestion) {
        throw new Error("Failed to get empty question");
      }
      
      // Create mapping of original IDs to new IDs for conditions update
      const questionIdMap = new Map();
      const answerIdMap = new Map();
      
      // Store mapping for the cloned question
      questionIdMap.set(questionToClone.id, emptyQuestion.id);
      
      // Clone the question data while using new ID
      const currentSection = data.form.sections[sectionIndex];
      const clonedQuestion = {
        ...questionToClone,
        id: emptyQuestion.id,
        questionCode: generateHierarchicalCodes.generateQuestionCode(
          currentSection.sectionCode, 
          currentSection.questions.length
        ),
        answers: [] // We'll add cloned answers with new IDs
      };
      
      // Add "(cloned)" or "(clonata)" to question text based on language
      if (clonedQuestion.questionText && Array.isArray(clonedQuestion.questionText)) {
        clonedQuestion.questionText = clonedQuestion.questionText.map(textItem => {
          const suffix = textItem.language === 'it' ? ' (clonata)' : ' (cloned)';
          return {
            ...textItem,
            text: textItem.text + suffix
          };
        });
      }
      
      // Clone each answer in the question
      for (let i = 0; i < questionToClone.answers?.length || 0; i++) {
        const answerToClone = questionToClone.answers[i];
        
        // Get empty answer to get new ID
        const emptyAnswer = await getEmptyEntity('answer');
        if (!emptyAnswer) {
          continue; // Skip this answer if we couldn't get a new ID
        }
        
        // Store mapping for condition updates
        answerIdMap.set(answerToClone.id, emptyAnswer.id);
        
        // Clone the answer data while using new ID
        const clonedAnswer = {
          ...answerToClone,
          id: emptyAnswer.id,
          answerCode: generateHierarchicalCodes.generateAnswerCode(
            clonedQuestion.questionCode, 
            clonedQuestion.answers.length
          )
        };
        
        clonedQuestion.answers.push(clonedAnswer);
      }

      // Update conditions in the cloned question to reference new IDs
      // Note: This will only update references to the cloned question itself
      // References to other questions will remain unchanged
      if (clonedQuestion.visibilityCondition) {
        clonedQuestion.visibilityCondition = updateConditionReferences(
          clonedQuestion.visibilityCondition, 
          questionIdMap, 
          answerIdMap
        );
      }
      
      // Add the cloned question to the section
      const updatedSections = [...data.form.sections];
      updatedSections[sectionIndex].questions.push(clonedQuestion);
      
      setData(prev => ({
        form: {
          ...prev.form,
          sections: updatedSections
        }
      }));
    } catch (err) {
      console.error("Error cloning question:", err);
    }
  };

  // Add cloneQuestionInSection function to handle cloning questions within a section
  const cloneQuestionInSection = async (sectionIndex, questionIndex) => {
    await cloneQuestion(sectionIndex, questionIndex);
  };

  /**
   * Handle User Guide File Update
   *
   * Updates the form data with the new user guide files information
   * Called when a guide file is uploaded or deleted
   *
   * @param {Array} files - Array of uploaded file objects (consistent with questions pattern)
   */
  const handleGuideFileUpdate = (files) => {
    setData(prev => ({
      form: {
        ...prev.form,
        userGuideFiles: files || []
      }
    }));
    setPreventQuit(true);
  };

  /**
   * Handle Collaborative Forms Schema Update
   *
   * Updates the form data with the new collaborative actors schema
   * Called when actors or question assignments change
   *
   * @param {Array} schema - Array of {actor: {id, name}, questions: [questionIds]}
   */
  const handleCollaborativeSchemaUpdate = (schema) => {
    setData(prev => ({
      form: {
        ...prev.form,
        CollaborativeFormsSchema: schema || []
      }
    }));
    setPreventQuit(true);
  };

  return (
    <div className="d-flex flex-column" style={{ height: '100vh' }}>
      
      {/* 1) FIXED HEADER + TOOLBAR (never scrolls) */}
      <header className="bg-white shadow-sm">
        <div className="container-fluid d-flex justify-content-between align-items-center py-2">
          
          {/* → breadcrumb navigation */}
          <ol className="breadcrumb mb-0"></ol>
          
          {/* → action buttons */}
          <div className="d-flex gap-3">
            {data.form?.Id && (
              <a 
                className="text-danger fs-13" 
                onClick={handleDeleteForm} 
                role="button" 
                title={t('deleteForm', { ns: 'labels' })}
              >
                <i className="fa-solid fa-trash-can fa-2x"></i>
              </a>
            )}
            <a 
              className="text-primary fs-13" 
              href="/form_builder/finder" 
              role="button" 
              title={t('searchForms', { ns: 'labels' })}
            >
              <i className="fa-solid fa-magnifying-glass fa-2x"></i>
            </a>
            <a 
              className="text-primary fs-13" 
              onClick={handleImportForm} 
              role="button" 
              title={t('importForm', { ns: 'labels' }) || 'Importa Form'}
            >
              <i className="fa-solid fa-download fa-2x"></i>
            </a>
            <a 
              className="text-primary fs-13" 
              onClick={handleExportForm} 
              role="button" 
              title={t('exportForm', { ns: 'labels' }) || 'Esporta Form'}
            >
              <i className="fa-solid fa-upload fa-2x"></i>
            </a>
            <a 
              className="text-primary fs-13" 
              onClick={handleSimulation} 
              role="button" 
              title={t('simulateForm', { ns: 'labels' })}
            >
              <i className="fa-solid fa-play fa-2x"></i>
            </a>
            <a 
              className="text-primary fs-13" 
              onClick={saveForm} 
              role="button" 
              title={t('saveForm', { ns: 'labels' })}
            >
              <i className="fa-solid fa-floppy-disk fa-2x"></i>
            </a>
          </div>
        </div>
      </header>

      {/* 2) SCROLLABLE CONTENT AREA */}
      <main className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
        <div className="container-fluid py-3">
          <div className="row">
            <div className="col-xl-12">
              <div className="card h-auto">
                <div className="card-header">
                  <h4 className="heading mb-0">
                    {data.form?.Id
                      ? (data.form.Name && data.form.Name.trim() !== ''
                        ? `${t('editForm', { ns: 'labels' })}: ${data.form.Name}`
                        : `${t('editForm', { ns: 'labels' })} #${data.form.Id}`)
                      : t('createNewForm', { ns: 'labels' })}
                  </h4>
                  {/* <div className="d-flex align-items-center">
                    <select
                      className="form-select form-select-sm me-3"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      aria-label={t('selectLanguage', { ns: 'labels' })}
                    >
                      <option value="en">{t('english', { ns: 'labels' })}</option>
                      <option value="it">{t('italian', { ns: 'labels' })}</option>
                    </select>
                  </div> */}
                </div>

                <div className="card-body p-3">
                  {loading && (
                    <div className="d-flex justify-content-center py-5">
                      <div className="spinner-border" style={{ color: Colors.primary }} role="status">
                        <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="alert alert-danger">
                      <strong>{t('error', { ns: 'labels' })}:</strong> {error}
                    </div>
                  )}

                  {showSimulation && !loading && data.form && (
                    <Simulation_Builder
                      form={data.form}
                      language={language}
                      onClose={() => setShowSimulation(false)}
                      colors={Colors}
                      mode="simulation"
                    />
                  )}

                  {!showSimulation && !loading && data.form && (
                    <div className="row g-4">
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header" style={{ backgroundColor: Colors.secondaryLight }}>
                            <h5 className="mb-0">{t('basicInformation', { ns: 'labels' })}</h5>
                          </div>
                          <div className="card-body">
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label className="form-label">
                                  {t('formName', { ns: 'labels' })}
                                  <span className="text-danger ms-1">*</span>
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={data.form.Name || ''}
                                  onChange={(e) => updateField("Name", e.target.value)}
                                  placeholder={t('enterFormName', { ns: 'labels' })}
                                  required
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">{t('formGroup', { ns: 'labels' })}</label>
                                <Choices
                                  ref={groupRef}
                                  voc={groupsVoc}
                                  value="Code"
                                  text="Name"
                                  className="inputStyle blackText bg-white"
                                  selected={data.form.Group ? [{ Code: data.form.Group }] : []}
                                  onChange={(e) => {
                                    const selected = e.selected?.[0]?.Code || "";
                                    updateField("Group", selected);
                                  }}
                                  placeholder={t('selectGroup', { ns: 'labels' })}
                                  dropDownParent="root"
                                />
                              </div>
                              <div className="col-md-6 w-100">
                                <label className="form-label">
                                  {t('formCode', { ns: 'labels' })}
                                  <span className="text-danger ms-1">*</span>
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={data.form.Code || ''}
                                  onChange={(e) => updateField("Code", e.target.value)}
                                  placeholder={t('enterFormCode', { ns: 'labels' })}
                                  required
                                />
                              </div>
                              <div className="col-md-12">
                                <label className="form-label">{t('formDescription', { ns: 'labels' })}</label>
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  value={data.form.Description || ''}
                                  onChange={(e) => updateField("Description", e.target.value)}
                                  placeholder={t('enterFormDescription', { ns: 'labels' })}
                                />
                              </div>
                              
                              <div className="col-md-12">
                                <label className="form-label fw-bold">{t('progressBarSettings', { ns: 'labels' })}</label>
                                <div className="border rounded p-3" style={{ backgroundColor: Colors.light }}>
                                  <div className="form-check mb-2 d-flex gap-2">
                                    <input
                                      className="form-check-input flex-shrink-0"
                                      type="checkbox"
                                      id="formShowFirstProgressBar"
                                      checked={data.form.showFirstProgressBar ?? true}
                                      onChange={(e) => updateField("showFirstProgressBar", e.target.checked)}
                                    />
                                    <div>
                                      <label className="form-check-label" htmlFor="formShowFirstProgressBar">
                                        {t('showFirstProgressBar', { ns: 'labels' })}
                                      </label>
                                      <div className="form-text text-muted small">
                                        {t('showFirstProgressBarDescription', { ns: 'labels' })}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="form-check mb-2 d-flex gap-2">
                                    <input
                                      className="form-check-input flex-shrink-0"
                                      type="checkbox"
                                      id="formShowSecondProgressBar"
                                      checked={data.form.showSecondProgressBar ?? true}
                                      onChange={(e) => updateField("showSecondProgressBar", e.target.checked)}
                                    />
                                    <div>
                                      <label className="form-check-label" htmlFor="formShowSecondProgressBar">
                                        {t('showSecondProgressBar', { ns: 'labels' })}
                                      </label>
                                      <div className="form-text text-muted small">
                                        {t('showSecondProgressBarDescription', { ns: 'labels' })}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="form-check d-flex gap-2">
                                    <input
                                      className="form-check-input flex-shrink-0"
                                      type="checkbox"
                                      id="formShowUserGuide"
                                      checked={data.form.showUserGuide ?? false}
                                      onChange={(e) => updateField("showUserGuide", e.target.checked)}
                                    />
                                    <div>
                                      <label className="form-check-label" htmlFor="formShowUserGuide">
                                        {t('showUserGuide', { ns: 'labels' })}
                                      </label>
                                      <div className="form-text text-muted small">
                                        {t('showUserGuideDescription', { ns: 'labels' })}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="col-md-12">
                                <label className="form-label fw-bold">{t('collaborativeFormsSettings', { ns: 'labels' })}</label>
                                <div className="border rounded p-3" style={{ backgroundColor: Colors.light }}>
                                  <div className="form-text text-muted small mb-3">
                                    {t('collaborativeFormsSettingsDescription', { ns: 'labels' })}
                                  </div>
                                  <div className="form-check d-flex gap-2 mb-3">
                                    <input
                                      className="form-check-input flex-shrink-0"
                                      type="checkbox"
                                      id="formIsCollaborative"
                                      checked={data.form.IsCollaborative ?? false}
                                      onChange={(e) => updateField("IsCollaborative", e.target.checked)}
                                    />
                                    <div>
                                      <label className="form-check-label" htmlFor="formIsCollaborative">
                                        {t('isCollaborative', { ns: 'labels' })}
                                      </label>
                                      <div className="form-text text-muted small">
                                        {t('isCollaborativeDescription', { ns: 'labels' })}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Collaborative Actors Manager - Show only when IsCollaborative is enabled */}
                                  {data.form.IsCollaborative && (
                                    <div className="mt-3">
                                      <CollaborativeActorsManager
                                        collaborativeFormsSchema={data.form.CollaborativeFormsSchema || []}
                                        onChange={handleCollaborativeSchemaUpdate}
                                        formSections={data.form.sections || []}
                                        colors={Colors}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* User Guide Manager - Show only when showUserGuide is enabled */}
                              {data.form.showUserGuide && (
                                <div className="col-md-12 mt-3">
                                  <UserGuideManager
                                    form={data.form}
                                    currentUser={currentUser}
                                    colors={Colors}
                                    onGuideFileUpdate={handleGuideFileUpdate}
                                    disabled={saving}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <Sections_Builder
                          sections={data.form.sections || []}
                          language={language}
                          onTitleChange={updateSectionTitle}
                          onDelete={deleteSection}
                          onAdd={addSection}
                          onUpdateSection={updateSection}
                          onReorder={handleSectionReorder}
                          onClone={cloneSection}
                          colors={Colors}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

/**
 * Utility functions for generating hierarchical codes for form elements
 */
export const generateHierarchicalCodes = {
  /**
   * Generate a section code based on its index
   * @param {number} sectionIndex - The index of the section in the form
   * @returns {string} - Generated code in format 's1', 's2', etc.
   */
  generateSectionCode: (sectionIndex) => {
    return `s${sectionIndex + 1}`;
  },

  /**
   * Generate a question code based on its section code and question index
   * @param {string} sectionCode - The code of the parent section
   * @param {number} questionIndex - The index of the question in the section
   * @returns {string} - Generated code in format 's1q1', 's1q2', etc.
   */
  generateQuestionCode: (sectionCode, questionIndex) => {
    return `${sectionCode}q${questionIndex + 1}`;
  },

  /**
   * Generate an answer code based on its question code and answer index
   * @param {string} questionCode - The code of the parent question
   * @param {number} answerIndex - The index of the answer in the question
   * @returns {string} - Generated code in format 's1q1a1', 's1q1a2', etc.
   */
  generateAnswerCode: (questionCode, answerIndex) => {
    return `${questionCode}a${answerIndex + 1}`;
  },

  /**
   * Update section codes in an entire form
   * @param {Array} sections - The sections array from the form
   * @returns {Array} - Updated sections with correct codes
   */
  updateAllCodes: (sections) => {
    return sections.map((section, sIndex) => {
      const sectionCode = generateHierarchicalCodes.generateSectionCode(sIndex);
      
      const updatedQuestions = (section.questions || []).map((question, qIndex) => {
        const questionCode = generateHierarchicalCodes.generateQuestionCode(sectionCode, qIndex);
        
        const updatedAnswers = (question.answers || []).map((answer, aIndex) => {
          return {
            ...answer,
            answerCode: generateHierarchicalCodes.generateAnswerCode(questionCode, aIndex)
          };
        });
        
        return {
          ...question,
          questionCode,
          answers: updatedAnswers
        };
      });
      
      return {
        ...section,
        sectionCode,
        questions: updatedQuestions
      };
    });
  }
};

export { Form_Builder, Colors };
